import { Alert, Dialog, DialogActions, DialogContent, Stack, useTheme } from "@mui/material";
import { Button, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

type Props = {
    open: boolean;
    busy: boolean;
    valid: boolean;
    saveFailed: boolean;
    onSave: () => void;
    onClose: () => void;
    onDiscard: () => void;
};

export const TemplateUnsavedChangesDialog = (props: Props) => {
    const { open, busy, valid, saveFailed, onSave, onClose, onDiscard } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const close = () => {
        if (!busy) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            aria-labelledby="template-unsaved-title"
            aria-describedby="template-unsaved-description"
            fullWidth
            maxWidth="sm"
        >
            <Stack sx={{ p: theme.spacing(4) }}>
                <Typography id="template-unsaved-title" variant="h3">
                    {t("settings.action_plan_templates.editor.unsaved", "Unsaved changes")}
                </Typography>
            </Stack>
            <DialogContent sx={{ px: theme.spacing(4), pb: theme.spacing(4) }}>
                <Stack gap={theme.spacing(2)}>
                    <Typography component="p" variant="body-regular" id="template-unsaved-description">
                        {t(
                            "settings.action_plan_templates.editor.unsaved_prompt",
                            "You have unsaved changes. What would you like to do?",
                        )}
                    </Typography>
                    {!valid && (
                        <Alert severity="info">
                            {t(
                                "settings.action_plan_templates.editor.complete_before_leaving",
                                'Complete the required fields before saving. Select "Keep editing" to correct them.',
                            )}
                        </Alert>
                    )}
                    {saveFailed && (
                        <Alert severity="error">
                            {t(
                                "settings.action_plan_templates.editor.save_failed",
                                "Unable to save the template. Check the fields and try again.",
                            )}
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    p: theme.spacing(4),
                    borderTop: 1,
                    borderColor: "divider",
                    flexDirection: "column",
                    gap: theme.spacing(2),
                    "& > :not(style) ~ :not(style)": { marginLeft: 0 },
                }}
            >
                <Button variant="primary" fullWidth disabled={busy || !valid} onClick={onSave}>
                    {t("settings.action_plan_templates.editor.save_and_leave", "Save and leave")}
                </Button>
                <Button variant="secondary" fullWidth disabled={busy} onClick={onDiscard}>
                    {t("settings.action_plan_templates.editor.leave_without_saving", "Leave without saving")}
                </Button>
                <Button variant="secondary" fullWidth disabled={busy} onClick={close}>
                    {t("settings.action_plan_templates.editor.keep_editing", "Keep editing")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
